/**
 * WebSocket Service — Staff Interface
 *
 * Manages Laravel Echo connections for real-time staff features.
 * Uses the Pusher protocol over soketi (cloud) / Reverb (local).
 *
 * Private channels:
 * - staff.tickets — Queue of all support tickets (.ticket.created, .ticket.status_changed, .ticket.reply_added)
 * - staff.chat — Live support-chat queue (.chat.session.escalated, .chat.session.status_changed) + kb-builder ingestion runs (.kb.ingestion.recorded)
 * - ticket.{uuid} — Individual ticket updates (.ticket.status_changed, .ticket.reply_added)
 * - chat.{uuid} — Individual chat session (.chat.message.received, .chat.session.status_changed)
 * - user.{uuid} — Personal staff notifications (.notification.created)
 */

// laravel-echo + pusher-js are loaded on demand inside initialize(): this
// module is imported from a boot file, so a static import would put both
// libraries in the initial bundle for every visitor, including the
// unauthenticated /login page that never opens a socket.
import type Echo from 'laravel-echo';
import { useOAuthStore } from 'src/stores/oauth';
import { devError, devLog } from '@shared/vue/utils/debug';

// ============================================
// CONFIGURATION
// ============================================

const getWebSocketConfig = () => ({
  key: import.meta.env.VITE_REVERB_APP_KEY || '',
  host: import.meta.env.VITE_REVERB_HOST || 'localhost',
  port: Number(import.meta.env.VITE_REVERB_PORT) || 8080,
  scheme: import.meta.env.VITE_REVERB_SCHEME || 'http',
  path: import.meta.env.VITE_REVERB_PATH || '',
  apiUrl: import.meta.env.VITE_API_URL || '',
});

// ============================================
// TYPES
// ============================================

export interface WebSocketStatus {
  connected: boolean;
  connecting: boolean;
  error: string | null;
}

export interface TicketStatusChangedPayload {
  ticket_uuid: string;
  old_status: string;
  new_status: string;
  changed_by_uuid: string;
  changed_at: string;
}

export interface TicketReplyAddedPayload {
  ticket_uuid: string;
  message_uuid: string;
  content: string;
  sender_name: string;
  sender_uuid: string;
  is_internal: boolean;
  created_at: string;
}

export interface TicketCreatedPayload {
  ticket_uuid: string;
  subject: string;
  priority: string;
  channel: string | null;
  user_name: string;
  user_uuid: string | null;
  created_at: string;
}

export interface NotificationCreatedPayload {
  notification: Record<string, unknown>;
  unread_count: number;
}

// Mirrors App\Events\Kb\KbIngestionRunRecorded::broadcastWith(). Scalar run
// summary only — the SI uses it to decide whether the KB review badge should
// re-fetch its authoritative pending-review count.
export interface KbIngestionRecordedPayload {
  uuid: string;
  run_type: string;
  status: string;
  articles_created: number;
  articles_updated: number;
  articles_forked: number;
}

// ============================================
// WEBSOCKET SERVICE CLASS
// ============================================

class WebSocketService {
  private echo: Echo | null = null;
  private initializing: Promise<Echo> | null = null;
  private status: WebSocketStatus = {
    connected: false,
    connecting: false,
    error: null,
  };
  private statusListeners: Set<(status: WebSocketStatus) => void> = new Set();

  /**
   * Initialize Echo. Async because laravel-echo + pusher-js are dynamically
   * imported on first use; concurrent callers share the one in-flight init.
   */
  async initialize(): Promise<Echo> {
    if (this.echo) {
      return this.echo;
    }
    if (this.initializing) {
      return this.initializing;
    }
    this.initializing = this.doInitialize();
    try {
      return await this.initializing;
    } finally {
      this.initializing = null;
    }
  }

  private async doInitialize(): Promise<Echo> {
    const config = getWebSocketConfig();

    this.status.connecting = true;
    this.notifyStatusChange();

    const [{ default: EchoCtor }, { default: Pusher }] = await Promise.all([
      import('laravel-echo'),
      import('pusher-js'),
    ]);

    // Echo reads the Pusher client off the global.
    window.Pusher = Pusher;

    // Disable Pusher's default console logging
    Pusher.logToConsole = false;

    const authorizer = (channel: { name: string }) => {
      return {
        authorize: (
          socketId: string,
          callback: (error: boolean, data: unknown) => void
        ) => {
          void this.authorizeChannel(channel.name, socketId, callback);
        },
      };
    };

    // Self-hosted soketi (Pusher protocol) via VITE_REVERB_* host/port/path, in
    // every environment. A managed-Pusher-Channels branch used to sit here for
    // "production", but no env file or deploy script has ever set
    // VITE_PUSHER_APP_KEY: populate-prod-env.sh renders
    // VITE_REVERB_HOST=ws.emalino.com and prod runs self-hosted soketi. The
    // operator chose soketi over Pusher SaaS, so the branch was unreachable and
    // its comment actively misleading during a realtime incident.
    this.echo = new EchoCtor({
      broadcaster: 'pusher',
      key: config.key,
      wsHost: config.host,
      wsPort: config.port,
      wssPort: config.port,
      wsPath: config.path,
      forceTLS: config.scheme === 'https',
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
      cluster: 'mt1', // Required by pusher-js; ignored by soketi
      authorizer,
    });

    // Listen for Pusher connection state changes
    const pusherConnection = this.echo.connector.pusher.connection;

    pusherConnection.bind('connected', () => {
      this.status.connected = true;
      this.status.connecting = false;
      this.status.error = null;
      this.notifyStatusChange();
    });

    pusherConnection.bind('disconnected', () => {
      this.status.connected = false;
      this.notifyStatusChange();
    });

    pusherConnection.bind('unavailable', () => {
      this.status.error = 'WebSocket server unavailable';
      this.status.connecting = false;
      this.notifyStatusChange();
    });

    pusherConnection.bind('failed', () => {
      this.status.error = 'WebSocket connection failed';
      this.status.connecting = false;
      this.notifyStatusChange();
    });

    pusherConnection.bind('error', (error: { error?: { data?: { message?: string } } }) => {
      this.status.error = error.error?.data?.message || 'WebSocket connection error';
      this.status.connecting = false;
      this.notifyStatusChange();
    });

    return this.echo;
  }

  /**
   * Custom authorizer for private/presence channels
   */
  private async authorizeChannel(
    channelName: string,
    socketId: string,
    callback: (error: boolean, data: unknown) => void
  ): Promise<void> {
    const authUrl = `${getWebSocketConfig().apiUrl}/broadcasting/auth`;
    devLog('[WS Auth] Authorizing channel:', channelName);

    try {
      const oAuthStore = useOAuthStore();
      const token = oAuthStore.getToken;

      if (!token?.accessToken) {
        devError('[WS Auth] No token for channel:', channelName);
        callback(true, { error: 'No authentication token available' });
        return;
      }

      let response = await this.postChannelAuth(authUrl, channelName, socketId, token);

      // An expired access token 401/403s here. This raw fetch bypasses the
      // api refresh interceptor, so without a retry the subscription silently
      // dies on idle (WS review §P3 #41). Refresh once (the shared oauth
      // store serializes + dedups refreshes across tabs/callers via the Web
      // Locks API) and retry with the rotated token before giving up.
      if (response.status === 401 || response.status === 403) {
        devLog('[WS Auth] Token rejected, refreshing and retrying:', channelName);
        try {
          await oAuthStore.refreshAccessToken();
          const refreshed = oAuthStore.getToken;
          if (refreshed?.accessToken) {
            response = await this.postChannelAuth(authUrl, channelName, socketId, refreshed);
          }
        } catch (refreshError) {
          devError('[WS Auth] Refresh failed during channel auth:', channelName, refreshError);
        }
      }

      if (!response.ok) {
        devError('[WS Auth] Failed for', channelName, '- status:', response.status);
        callback(true, { error: `Authorization failed with status ${response.status}` });
        return;
      }

      devLog('[WS Auth] Authorized:', channelName);
      const data = await response.json();
      callback(false, data);
    } catch (error) {
      devError('[WS Auth] Error for', channelName, error);
      callback(true, { error: 'Channel authorization error' });
    }
  }

  /**
   * POST a single /broadcasting/auth request with the given token. Split out
   * so the authorizer can retry it after a token refresh.
   */
  private postChannelAuth(
    authUrl: string,
    channelName: string,
    socketId: string,
    token: { accessToken: string; tokenType?: string | null },
  ): Promise<Response> {
    // Default the scheme to 'Bearer' — an empty tokenType would produce a
    // malformed " <token>" header (leading space), which the API rejects as
    // a 401 rather than a clean auth failure, masking the real cause.
    const tokenType = token.tokenType || 'Bearer';

    return fetch(authUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `${tokenType} ${token.accessToken}`,
        'X-EMALINO-Correlation-Id': crypto.randomUUID(),
      },
      body: JSON.stringify({
        socket_id: socketId,
        channel_name: channelName,
      }),
      credentials: 'include',
    });
  }

  /**
   * Get Echo instance
   */
  getEcho(): Echo | null {
    return this.echo;
  }

  /**
   * The current Echo/Pusher socket id, or null if Echo hasn't connected yet.
   * Sent as the `X-Socket-ID` request header so the server's
   * `broadcast()->toOthers()` excludes the originating client; without it a
   * staff user receives the realtime echo of their own write.
   */
  getSocketId(): string | null {
    try {
      return this.echo?.socketId() ?? null;
    } catch {
      // socketId() reads the connector's connection, which is undefined until
      // the socket finishes connecting; treat any pre-connect access as null.
      return null;
    }
  }

  /**
   * Get connection status
   */
  getStatus(): WebSocketStatus {
    return { ...this.status };
  }

  /**
   * Subscribe to status changes
   */
  onStatusChange(listener: (status: WebSocketStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /**
   * Notify all status listeners
   */
  private notifyStatusChange(): void {
    const status = this.getStatus();
    this.statusListeners.forEach((listener) => listener(status));
  }

  /**
   * Subscribe to the staff ticket queue channel (staff only).
   * Receives real-time notifications for new tickets, status changes,
   * and replies across all tickets in the queue.
   */
  /**
   * Returns a teardown that removes ONLY the listeners this call registered,
   * leaving the shared `staff.tickets` channel (and any other subscribers'
   * listeners, e.g. the boot-level toast) intact. Callers that mount/unmount
   * (pages) MUST use this teardown — the shared channel itself is only torn
   * down wholesale on logout, via `disconnect()` (which closes the socket and
   * drops every channel without per-channel unsubscribe frames).
   */
  subscribeToStaffTicketQueue(
    handlers: {
      onTicketCreated?: (data: TicketCreatedPayload) => void;
      onStatusChanged?: (data: TicketStatusChangedPayload) => void;
      onReplyAdded?: (data: TicketReplyAddedPayload) => void;
      onError?: (error: unknown) => void;
      onSubscribed?: () => void;
    }
  ): () => void {
    if (!this.echo) return () => {};

    const channel = this.echo.private('staff.tickets');

    channel
      .subscribed(() => {
        handlers.onSubscribed?.();
      })
      .error((error: unknown) => {
        handlers.onError?.(error);
      });

    const teardowns: Array<() => void> = [];
    if (handlers.onTicketCreated) {
      const handler = handlers.onTicketCreated;
      channel.listen('.ticket.created', handler);
      teardowns.push(() => channel.stopListening('.ticket.created', handler));
    }
    if (handlers.onStatusChanged) {
      const handler = handlers.onStatusChanged;
      channel.listen('.ticket.status_changed', handler);
      teardowns.push(() => channel.stopListening('.ticket.status_changed', handler));
    }
    if (handlers.onReplyAdded) {
      const handler = handlers.onReplyAdded;
      channel.listen('.ticket.reply_added', handler);
      teardowns.push(() => channel.stopListening('.ticket.reply_added', handler));
    }

    return () => {
      teardowns.forEach((teardown) => teardown());
    };
  }

  /**
   * Returns a teardown that removes ONLY this call's listeners, leaving the
   * shared `staff.chat` channel (and the boot-level escalation toast) intact.
   * Pages MUST use this teardown — the shared channel is torn down wholesale
   * only on logout, via `disconnect()`.
   */
  subscribeToChatQueue(
    handlers: {
      onSessionEscalated?: (data: { session_uuid: string; reason?: string; topic?: string; priority?: string; language?: string }) => void;
      onStatusChanged?: (data: { session_uuid: string; status: string }) => void;
      onError?: (error: unknown) => void;
      onSubscribed?: () => void;
    },
  ): () => void {
    if (!this.echo) return () => {};

    const channel = this.echo.private('staff.chat');

    channel
      .subscribed(() => {
        handlers.onSubscribed?.();
      })
      .error((error: unknown) => {
        handlers.onError?.(error);
      });

    const teardowns: Array<() => void> = [];
    if (handlers.onSessionEscalated) {
      const handler = handlers.onSessionEscalated;
      channel.listen('.chat.session.escalated', handler);
      teardowns.push(() => channel.stopListening('.chat.session.escalated', handler));
    }
    if (handlers.onStatusChanged) {
      const handler = handlers.onStatusChanged;
      channel.listen('.chat.session.status_changed', handler);
      teardowns.push(() => channel.stopListening('.chat.session.status_changed', handler));
    }

    return () => {
      teardowns.forEach((teardown) => teardown());
    };
  }

  /**
   * Subscribe to kb-builder ingestion runs on the shared `staff.chat` channel
   * so the KB review badge updates live instead of only polling on mount.
   *
   * Shares `staff.chat` with subscribeToChatQueue + the boot escalation toast,
   * so the teardown only `stopListening`s its own handler (the P0.5 invariant)
   * — it must never `leave` the channel, or the co-tenant subscribers die.
   */
  subscribeToKbIngestion(
    handlers: {
      onIngestionRecorded?: (data: KbIngestionRecordedPayload) => void;
      onError?: (error: unknown) => void;
      onSubscribed?: () => void;
    },
  ): () => void {
    if (!this.echo) return () => {};

    const channel = this.echo.private('staff.chat');

    channel
      .subscribed(() => {
        handlers.onSubscribed?.();
      })
      .error((error: unknown) => {
        handlers.onError?.(error);
      });

    const teardowns: Array<() => void> = [];
    if (handlers.onIngestionRecorded) {
      const handler = handlers.onIngestionRecorded;
      channel.listen('.kb.ingestion.recorded', handler);
      teardowns.push(() => channel.stopListening('.kb.ingestion.recorded', handler));
    }

    return () => {
      teardowns.forEach((teardown) => teardown());
    };
  }

  subscribeToChatSession(
    sessionUuid: string,
    handlers: {
      onMessageReceived?: (data: { session_uuid: string; message_uuid: string; sender_type: string; content: string }) => void;
      onStatusChanged?: (data: { session_uuid: string; status: string }) => void;
      onError?: (error: unknown) => void;
    },
  ): () => void {
    if (!this.echo) return () => {};

    const channel = this.echo.private(`chat.${sessionUuid}`);

    if (handlers.onMessageReceived) {
      channel.listen('.chat.message.received', handlers.onMessageReceived);
    }
    if (handlers.onStatusChanged) {
      channel.listen('.chat.session.status_changed', handlers.onStatusChanged);
    }
    channel.error((error: unknown) => {
      handlers.onError?.(error);
    });

    // Per-resource channel (one drawer owns it), so the teardown leaves the
    // whole channel — unlike the shared staff.* queue subscribers, which only
    // stopListening their own handlers (the P0.5 invariant).
    return () => this.unsubscribeFromChatSession(sessionUuid);
  }

  unsubscribeFromChatSession(sessionUuid: string): void {
    if (!this.echo) return;
    this.echo.leave(`chat.${sessionUuid}`);
  }

  /**
   * Subscribe to a specific support ticket channel.
   * Available to staff and the ticket owner.
   */
  subscribeToTicket(
    ticketUuid: string,
    handlers: {
      onStatusChanged?: (data: TicketStatusChangedPayload) => void;
      onReplyAdded?: (data: TicketReplyAddedPayload) => void;
      onError?: (error: unknown) => void;
      onSubscribed?: () => void;
    }
  ): () => void {
    if (!this.echo) return () => {};

    const channel = this.echo.private(`ticket.${ticketUuid}`);

    channel
      .subscribed(() => {
        handlers.onSubscribed?.();
      })
      .error((error: unknown) => {
        handlers.onError?.(error);
      });

    if (handlers.onStatusChanged) {
      channel.listen('.ticket.status_changed', handlers.onStatusChanged);
    }
    if (handlers.onReplyAdded) {
      channel.listen('.ticket.reply_added', handlers.onReplyAdded);
    }

    // Per-resource channel: the teardown leaves the whole channel (no other
    // caller shares ticket.{uuid}), unlike the shared staff.* queue subscribers.
    return () => this.unsubscribeFromTicket(ticketUuid);
  }

  /**
   * Unsubscribe from a specific ticket channel
   */
  unsubscribeFromTicket(ticketUuid: string): void {
    if (!this.echo) return;
    this.echo.leave(`ticket.${ticketUuid}`);
  }

  /**
   * Subscribe to a user's personal notification channel
   */
  subscribeToUserNotifications(
    userUuid: string,
    handlers: {
      onNotification?: (data: NotificationCreatedPayload) => void;
      onError?: (error: unknown) => void;
      onSubscribed?: () => void;
    }
  ): void {
    if (!this.echo) return;

    const channel = this.echo.private(`user.${userUuid}`);

    channel
      .subscribed(() => {
        handlers.onSubscribed?.();
      })
      .error((error: unknown) => {
        handlers.onError?.(error);
      });

    if (handlers.onNotification) {
      channel.listen('.notification.created', handlers.onNotification);
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.status.connected;
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
      this.status.connected = false;
      this.status.connecting = false;
      this.notifyStatusChange();
    }
  }

  /**
   * Reconnect to WebSocket server
   */
  reconnect(): Promise<Echo> {
    this.disconnect();
    return this.initialize();
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();

// Export class for testing
export { WebSocketService };
